import React, { useCallback } from 'react';
import * as Yup from 'yup';
import Textarea from '../../../components/form-elements/Textarea';
import Checkbox from '../../../components/form-elements/Checkbox';
import Input from '../../../components/form-elements/Input';
import Select from '../../../components/form-elements/Select';
import { InternalLink } from '../../../components/form-elements/Link';

const validationSchema = Yup.object().shape({
    isCompany: Yup.boolean(),
    company: Yup.string().when('isCompany', {
        is: true,
        then: Yup.string().required('Pflichtfeld'),
    }),
    salutation: Yup.string().required('Pflichtfeld'),
    lastName: Yup.string().required('Pflichtfeld'),
    email: Yup.string().email('E-Mail ungültig').required('Pflichtfeld'),
    comment: Yup.string(),
});

function InactiveChildren({ values }) {
    return (
        <>
            {values.isCompany && (
                <dl className="uk-description-list">
                    <dt>Firmenname:</dt>
                    <dd>{values.company}</dd>
                </dl>
            )}
            {(values.salutation || values.lastName) && (
                <dl className="uk-description-list">
                    <dt>Name:</dt>
                    <dd>
                        {values.salutation} {values.lastName}
                    </dd>
                </dl>
            )}
            {values.email && (
                <dl className="uk-description-list">
                    <dt>E-Mail:</dt>
                    <dd>{values.email}</dd>
                </dl>
            )}
            {values.comment && (
                <dl className="uk-description-list">
                    <dt>Notiz:</dt>
                    <dd style={{ whiteSpace: 'pre' }}>{values.comment}</dd>
                </dl>
            )}
        </>
    );
}

function ActiveChildren({ values, setFieldValue, handleBlur, touched, errors }) {
    const onChange = useCallback(({ name, value }) => setFieldValue(name, value), [setFieldValue]);

    return (
        <>
            <Checkbox name="isCompany" value={values.isCompany} label="Firma" onChange={onChange} onBlur={handleBlur} />
            {values.isCompany && (
                <Input
                    name="company"
                    value={values.company}
                    label="Firmenname *"
                    onChange={onChange}
                    onBlur={handleBlur}
                    error={touched.company && errors.company}
                    placeholder="Firmenname"
                />
            )}
            <Select
                name="salutation"
                value={values.salutation}
                label="Anrede *"
                onChange={onChange}
                onBlur={handleBlur}
                error={touched.salutation && errors.salutation}
                options={[
                    {
                        label: 'Anrede wählen',
                        value: '',
                    },
                    { label: 'Frau', value: 'Frau' },
                    { label: 'Herr', value: 'Herr' },
                    { label: 'Divers', value: 'Divers' },
                ]}
            />
            <Input
                name="lastName"
                value={values.lastName}
                label="Nachname *"
                onChange={onChange}
                onBlur={handleBlur}
                error={touched.lastName && errors.lastName}
                placeholder="Nachname"
            />
            <Input
                name="email"
                value={values.email}
                label="E-Mail *"
                onChange={onChange}
                onBlur={handleBlur}
                error={touched.email && errors.email}
                type="email"
            />
            <Textarea
                name="comment"
                value={values.comment}
                label="Kommentar"
                onChange={onChange}
                onBlur={handleBlur}
                error={touched.comment && errors.comment}
                placeholder={`Z.B.: Ich möchte den Service des Hol- und Bringdienstes in Anspruch nehmen 
                oder ich wünsche einen Rückruf unter der Telefonnummer 0123456789.`}
            />
            <div className="uk-margin">
                <div>
                    Ich habe die <InternalLink to="/datenschutz">Datenschutzhinweise</InternalLink> zur Kenntnis genommen.
                </div>
                <div>Ihre Daten werden ausschließlich für die Beantwortung dieser Anfrage verwendet.</div>
            </div>
        </>
    );
}

export const userSelectorStepFactory = ({ user = {} }) => {
    return {
        inactiveChildren: props => <InactiveChildren {...props} />,
        activeChildren: props => <ActiveChildren {...props} />,
        title: 'Daten',
        initialValues: {
            isCompany: user.isCompany ?? false,
            company: user.company ?? '',
            salutation: user.salutation ?? '',
            lastName: user.lastName ?? '',
            email: user.email ?? '',
            comment: '',
        },
        validationSchema,
    };
};
